import { getBlogPost, getBlogPosts } from './blog';
import { getResearchPost, getResearchPosts } from './research';
import { getCaseStudy, getCaseStudies } from './case-studies';

export type PostType = 'blog' | 'research' | 'case-study';

export interface RelatedPost {
  slug: string;
  type: PostType;
  title: string;
  excerpt: string;
  url: string;
}

const TYPE_URL_PREFIX: Record<PostType, string> = {
  blog: '/resources/blog',
  research: '/resources/research',
  'case-study': '/resources/case-studies',
};

/**
 * Parse a related ref like "case-study:lukes-journey" or just "my-slug".
 * Without prefix, defaults to `defaultType`.
 */
function parseRef(ref: string, defaultType: PostType): { type: PostType; slug: string } {
  const prefixes: PostType[] = ['case-study', 'research', 'blog'];
  for (const prefix of prefixes) {
    if (ref.startsWith(`${prefix}:`)) {
      return { type: prefix, slug: ref.slice(prefix.length + 1) };
    }
  }
  return { type: defaultType, slug: ref };
}

/** Look up a post by type and slug, returning a RelatedPost or undefined. */
function lookupPost(type: PostType, slug: string): RelatedPost | undefined {
  let title: string | undefined;
  let excerpt: string | undefined;

  if (type === 'blog') {
    const p = getBlogPost(slug);
    if (p) { title = p.title; excerpt = p.excerpt; }
  } else if (type === 'research') {
    const p = getResearchPost(slug);
    if (p) { title = p.title; excerpt = p.excerpt; }
  } else if (type === 'case-study') {
    const p = getCaseStudy(slug);
    if (p) { title = p.title; excerpt = p.excerpt; }
  }

  if (!title) return undefined;

  return {
    slug,
    type,
    title,
    excerpt: excerpt || '',
    url: `${TYPE_URL_PREFIX[type]}/${slug}`,
  };
}

/** Convert a same-type post to a RelatedPost. */
function toRelated(type: PostType, post: { slug: string; title: string; excerpt: string }): RelatedPost {
  return {
    slug: post.slug,
    type,
    title: post.title,
    excerpt: post.excerpt,
    url: `${TYPE_URL_PREFIX[type]}/${post.slug}`,
  };
}

/**
 * Get related posts for any article type.
 * Priority: explicit `related` refs (cross-type) → same-type tag matches → same-type most recent.
 */
export function getRelatedPosts(
  currentType: PostType,
  currentSlug: string,
  currentTags: string[],
  explicitRelated: string[] | undefined,
  limit = 2,
): RelatedPost[] {
  // 1. Explicit related refs from frontmatter (supports cross-type)
  if (explicitRelated?.length) {
    const explicit = explicitRelated
      .map((ref) => {
        const { type, slug } = parseRef(ref, currentType);
        return lookupPost(type, slug);
      })
      .filter((p): p is RelatedPost => p != null);
    if (explicit.length >= limit) return explicit.slice(0, limit);

    // Fill remaining from same-type
    const usedSlugs = new Set(explicit.map((p) => `${p.type}:${p.slug}`));
    usedSlugs.add(`${currentType}:${currentSlug}`);
    const rest = getSameTypePosts(currentType, currentSlug)
      .filter((p) => !usedSlugs.has(`${currentType}:${p.slug}`))
      .slice(0, limit - explicit.length)
      .map((p) => toRelated(currentType, p));
    return [...explicit, ...rest];
  }

  // 2. Same-type posts sharing at least one tag
  const sameTypePosts = getSameTypePosts(currentType, currentSlug);
  const tagged = sameTypePosts
    .filter((p) => p.tags.some((t: string) => currentTags.includes(t)))
    .map((p) => toRelated(currentType, p));
  if (tagged.length >= limit) return tagged.slice(0, limit);

  // 3. Fall back to same-type most recent
  return sameTypePosts.slice(0, limit).map((p) => toRelated(currentType, p));
}

/** Get sorted posts of a given type, excluding the current slug. */
function getSameTypePosts(type: PostType, excludeSlug: string) {
  if (type === 'blog') return getBlogPosts().filter((p) => p.slug !== excludeSlug);
  if (type === 'research') return getResearchPosts().filter((p) => p.slug !== excludeSlug);
  return getCaseStudies().filter((p) => p.slug !== excludeSlug);
}
